import { AttributeFormatDc, GeometryType, StringSubType } from "@evergis/api";
import { IOption } from "@evergis/ui";
import { EditGeometryType } from "../../../../../evergis/core/attributes/geometryTypes";
import { i18n } from "../../../../../i18n";
import { FormStateType } from "../hooks/useAttributeFormat";
import { TIME_ZONE_FORMAT } from "../hooks/useUpdateAttributeFormat";
import { dateOptions, numberOptions, pointOptions, timeOptions } from "./FormatSettingsOptions";

export const UPDATE_FORM_STATE = "updateFormState";
export const SET_FORM_STATE = "setFormState";

type FormAction = {
  type: string;
  payload?: FormStateType;
};

export function reducer(state: FormStateType, action: FormAction) {
  switch (action.type) {
    case UPDATE_FORM_STATE:
      return { ...state, ...action.payload };

    case SET_FORM_STATE:
      return { ...action.payload };

    default:
      return state;
  }
}

export const findEqualScalingValue = (format: number) => {
  const option = numberOptions.find(({ value }) => format.toExponential() === (+value).toExponential());

  return (option && option.value) || numberOptions[0].value;
};

export const getValueFromOptions = (options: IOption[], format: string) =>
  options.find(({ value }) => format && format.includes(value));

export const getPointFormatSettings = (stringFormat?: AttributeFormatDc) => {
  if (!(stringFormat && stringFormat.format)) {
    return {
      format: pointOptions[2].value,
      rounding: 2,
    };
  }

  const { format } = stringFormat;

  return {
    format: getValueFromOptions(pointOptions, format).value,
    rounding: format.includes(pointOptions[2].value) ? format.split(pointOptions[2].value).length : null,
  };
};

export const getDateFormatSettings = (stringFormat?: AttributeFormatDc) => {
  if (!stringFormat || !stringFormat.format) {
    return {
      format: "",
      timeFormat: "",
      showTime: false,
      showTimeZone: false,
    };
  }

  const { format } = stringFormat;
  const timeFormat = getValueFromOptions(timeOptions, format);
  const showTimeZone = format.includes(TIME_ZONE_FORMAT);
  const dateFormatOption = getValueFromOptions(dateOptions, format);

  return {
    format: dateFormatOption && dateFormatOption.value,
    timeFormat: (timeFormat && timeFormat.value) || "",
    showTime: !!timeFormat,
    showTimeZone,
  };
};

export const getNumberFormatSettings = (
  geometryType: GeometryType | EditGeometryType,
  stringFormat?: AttributeFormatDc,
  isCalculated?: boolean,
) => {
  if (!stringFormat || !stringFormat.scalingFactor) {
    return {
      format: "",
      rounding: 2,
      label: isCalculated
        ? (geometryType === GeometryType.Polyline && i18n.t("meter", { ns: "common" })) ||
          i18n.t("sqMeter", { ns: "common" })
        : "",
      splitDigitGroup: true,
    };
  }
  const { scalingFactor, rounding, splitDigitGroup, unitsLabel } = stringFormat;

  return {
    format: findEqualScalingValue(scalingFactor),
    label: unitsLabel,
    rounding,
    splitDigitGroup,
  };
};

export const getSubTypeDisplayText = (subType: StringSubType) => {
  return subType === StringSubType.Image
    ? i18n.t("imageGallery", { ns: "common" })
    : i18n.t("defaultValue", { ns: "common" });
};
